import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"
import { useToaster } from "@hellocontento/maillard"
import { ChevronDown, Layers } from "styled-icons/feather"
import { Clock } from "@styled-icons/feather/Clock"
import { CheckCircle } from "@styled-icons/heroicons-solid/CheckCircle"
import { InfoCircle } from "@styled-icons/bootstrap/InfoCircle"

import ContentTypeBadge from "components/common/contentTypes/ContentTypeBadge"
import defaultAvatar from "assets/images/dashboard/avatar-empty-state.png"
import { callApi } from "utils/ContentoApi"
import { theme } from "theme"

import ConfirmModal from "./ConfirmModal"
import DeletePostModal from "./DeletePostModal"
import RecreateVisualModal, { RecreateVisualMode } from "./RecreateVisualModal"
import PicturePickerModal from "./PicturePickerModal"
import ChannelListModal, { ChannelListEntry } from "./ChannelListModal"
import Tooltip, { renderWithLinks } from "./Tooltip"
import LinkedInPostPreview from "./LinkedInPostPreview"
// @ts-ignore — untyped JS util shared with the calendar/composer
import { getExternalPostUrl } from "utils/channels"
import * as styles from "../styles"
import {
  truncateText,
  formatScheduledDate,
  formatSourcePageDate,
  shortenSourceUrl,
  resolveEditAction,
  getVisitablePosts,
  describeEpisode,
  EditTarget,
  PostDisplayStatus,
  PostRefs,
  PostSeries,
  ScheduledPostRef,
  SourcePage,
} from "../utils"
import { CATEGORY_TO_CONTENT_TYPE } from "../values"
import { CreationChannel } from "../types"

// callApi tags HTTP 400/409 responses as a ValidationError carrying the server's reason
// (e.g. no connected channel, or no planned date) — the part worth showing the user.
const isValidationError = (err: unknown): err is Error =>
  err instanceof Error && err.name === "ValidationError"

export type Post = PostRefs & {
  id: string
  category: string | null
  date: string | null
  text: string | null
  image: { url: string; mimeType?: string; html?: string } | null
  displayStatus: PostDisplayStatus
  duplicateMessage?: string | null //May contain markdown-style inline links — `[text](url)` — which are rendered as clickable anchors in the tooltip.
  regeneratingVisual?: boolean
  visualRegenFailure?: string | null // why the last picked picture could not be applied; null once a new regeneration starts
  currentPictureId?: string | null // library picture behind the shown visual; null when none or the draft image was replaced
  pictureEligible: boolean // false for series episodes: only standalone posts and teasers carry a picture
  // Set when the post belongs to a series; recreating its visual redoes the whole series.
  seriesId?: string | null
  series?: PostSeries | null
  sourcePage?: SourcePage | null // website page the post was written from; null for uploaded files
  createdAt: string
  updatedAt: string
}

type PostRowProps = {
  accountId: string
  // Undefined for a batch that hasn't finished creating its own profile yet — the
  // "Change picture" pill only shows once there's a profile whose library it can read.
  profileId?: string | null
  post: Post
  timezone: string
  leadChannel?: CreationChannel | null
  accountChannels?: CreationChannel[]
  reserveSeriesSlot: boolean // set in batches holding a series, so every row's status pill lines up
  onDeleted: (postId: string) => void
  onUpdated: (post: Post) => void
}

const PostRow: React.FC<PostRowProps> = ({
  accountId,
  profileId,
  post,
  timezone,
  leadChannel,
  accountChannels = [],
  reserveSeriesSlot,
  onDeleted,
  onUpdated,
}) => {
  const history = useHistory()
  const location = useLocation()
  const addToast = useToaster()
  const userId = useSelector((state: any) => state.auth?.currentUser?.id)

  const [isExpanded, setIsExpanded] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isScheduling, setIsScheduling] = useState(false)
  // null = modal closed; otherwise the reason approval failed (shown in an info modal).
  const [scheduleError, setScheduleError] = useState<string | null>(null)
  // null = modal closed; otherwise which variant (single / series / blocked) is shown.
  const [recreateMode, setRecreateMode] = useState<RecreateVisualMode | null>(null)
  const [showPicturePicker, setShowPicturePicker] = useState(false)
  // A picked picture on a series post waits here until the series-wide regeneration is confirmed.
  const [pendingPictureId, setPendingPictureId] = useState<string | null>(null)
  const [showEditPicker, setShowEditPicker] = useState(false)
  const [showVisitPicker, setShowVisitPicker] = useState(false)

  // Toast a pick failure only when the poll delivers a new one: seeded with the mount-time value so
  // a reload of a post that already carries a reason stays quiet.
  const shownRegenFailure = useRef(post.visualRegenFailure)
  useEffect(() => {
    if (post.visualRegenFailure && post.visualRegenFailure !== shownRegenFailure.current) {
      addToast(`Couldn't use the picked picture: ${post.visualRegenFailure}`, "error")
    }
    shownRegenFailure.current = post.visualRegenFailure
  }, [post.visualRegenFailure, addToast])

  // Track the post being edited (draftId or postId)
  const editingPostIdRef = useRef<string | null>(null)

  const accountName = leadChannel?.username ?? "Your Company"
  const accountAvatar = leadChannel?.avatar || defaultAvatar

  const status = post.displayStatus
  const statusConfig = styles.STATUS_BADGE_CONFIG[status]
  const contentTypeId = post.category ? CATEGORY_TO_CONTENT_TYPE[post.category] : null
  const previewText = truncateText(post.text, 45)
  const showDuplicate = !!post.duplicateMessage && status !== "published"
  const episodeDescription = post.series ? describeEpisode(post.series) : undefined
  const sourceLabel = post.sourcePage ? shortenSourceUrl(post.sourcePage.url) : undefined
  const sourceDate = post.sourcePage ? formatSourcePageDate(post.sourcePage, timezone) : undefined
  const sourceInfo = post.sourcePage && sourceLabel ? (
    <Tooltip
      content={
        <>
          Source:{" "}
          <styles.TooltipLink href={post.sourcePage.url} target="_blank" rel="noopener noreferrer">
            {sourceLabel}
          </styles.TooltipLink>
          {sourceDate && ` · ${sourceDate}`}
        </>
      }
    >
      <styles.SourceInfoButton
        type="button"
        aria-label={`Source: ${sourceLabel}${sourceDate ? ` · ${sourceDate}` : ""}`}
        data-testid={`post-source-info-${post.id}`}
      >
        <InfoCircle size={16} />
      </styles.SourceInfoButton>
    </Tooltip>
  ) : undefined

  // Get current editing ID from URL (draftId or postId)
  const urlParams = new URLSearchParams(location.search)
  const currentPostId = urlParams.get("draftId") || urlParams.get("postId")

  // Detect when composer closes (editing param removed from URL) after this post was edited
  useEffect(() => {
    if (editingPostIdRef.current && !currentPostId) {
      editingPostIdRef.current = null

      const refetchPost = async () => {
        try {
          const response = await callApi(
            { method: "get", url: `content-factory/accounts/${accountId}/posts/${post.id}` },
            true,
            true,
          )
          if (response.post) {
            onUpdated(response.post)
          }
        } catch (err) {
          console.error("Failed to refetch post after edit", { postId: post.id, err })
        }
      }

      refetchPost()
    }
  }, [currentPostId, accountId, post.id, onUpdated])

  const findChannel = useCallback(
    (channelId?: string | null): CreationChannel | null =>
      accountChannels.find((accountChannel) => accountChannel.id === channelId) ?? null,
    [accountChannels],
  )

  const openEditTarget = useCallback((target: EditTarget) => {
    editingPostIdRef.current = target.id
    history.push({ search: target.kind === "draft" ? `?draftId=${target.id}` : `?postId=${target.id}` })
  }, [history])

  const editAction = useMemo(() => resolveEditAction(post), [post])

  const handleEdit = useCallback(() => {
    switch (editAction.kind) {
      case "open":
        openEditTarget(editAction.target)
        return
      case "pick":
        setShowEditPicker(true)
        return
      case "none":
        addToast("Please connect a channel first to edit this post.", "error")
    }
  }, [editAction, addToast, openEditTarget])

  const editPickerEntries: Array<ChannelListEntry & { target: EditTarget }> = useMemo(
    () =>
      (editAction.kind === "pick" ? editAction.targets : []).flatMap((target) => {
        const channelIds = target.channelIds.length > 0 ? target.channelIds : [null]
        return channelIds.map((channelId) => {
          const entryChannel = findChannel(channelId)
          return {
            key: `${target.kind}-${target.id}-${channelId ?? "none"}`,
            channel: entryChannel,
            label: entryChannel?.username ?? "Unknown channel",
            hint: target.kind === "draft" ? "Draft" : "Scheduled",
            target,
          }
        })
      }),
    [editAction, findChannel],
  )

  const handleSchedule = useCallback(async () => {
    if (isScheduling) return
    setIsScheduling(true)
    try {
      const response = await callApi(
        { method: "put", url: `content-factory/accounts/${accountId}/posts/${post.id}/scheduled`, data: { userId } },
        true,
        true,
      )
      if (response.post) {
        onUpdated(response.post)
      }
      addToast("Post scheduled.", "success")
    } catch (err) {
      console.error("Failed to schedule post", { accountId, postId: post.id, err })
      setScheduleError(isValidationError(err) ? err.message : "Failed to schedule post.")
    } finally {
      setIsScheduling(false)
    }
  }, [accountId, post.id, userId, onUpdated, addToast, isScheduling])

  // Delete post - API call with optional deletion reason and notify parent
  const handleDelete = useCallback(async (deletionReason: string) => {
    try {
      await callApi(
        { method: "delete", url: `content-factory/accounts/${accountId}/posts/${post.id}`, data: deletionReason ? { deletionReason } : undefined },
        true,
        true,
      )
      onDeleted(post.id)
    } catch (err) {
      console.error("Failed to delete post", { accountId, postId: post.id, err })
      addToast("Failed to delete post.", "error")
    }
    setShowDeleteModal(false)
  }, [accountId, post.id, onDeleted, addToast])

  const openExternalPost = useCallback((sourceId: string | null, postChannel: CreationChannel | null) => {
    const url = sourceId && postChannel ? getExternalPostUrl({ sourceId, channel: postChannel }) : null
    if (!url) {
      addToast("Cannot visit post - no external link available.", "error")
      return
    }
    window.open(url, "_blank")
  }, [addToast])

  const handleVisit = useCallback(() => {
    const visitablePosts = getVisitablePosts(post)
    if (visitablePosts.length > 1) {
      setShowVisitPicker(true)
      return
    }
    openExternalPost(visitablePosts[0]?.sourceId ?? null, findChannel(visitablePosts[0]?.channelId) ?? leadChannel ?? null)
  }, [post, leadChannel, findChannel, openExternalPost])

  const visitPickerEntries: Array<ChannelListEntry & { scheduledPost: ScheduledPostRef }> = useMemo(
    () =>
      getVisitablePosts(post).map((scheduledPost) => {
        const entryChannel = findChannel(scheduledPost.channelId)
        return {
          key: scheduledPost.id,
          channel: entryChannel,
          label: entryChannel?.username ?? "Unknown channel",
          scheduledPost,
        }
      }),
    [post, findChannel],
  )

  // Confirm a single-post or whole-series regeneration. A series with an already-published
  // episode is blocked by the server (409), which swaps the modal to its blocked variant.
  const openRecreateVisualModal = useCallback(() => {
    setRecreateMode(post.seriesId ? "series" : "single")
  }, [post.seriesId])

  const openPicturePickerModal = useCallback(() => {
    setShowPicturePicker(true)
  }, [])

  // Change picture - same regeneration job as Recreate, forced to the picked picture instead
  // of the ranked auto-pick. Same optimistic regeneratingVisual flag and poll-reconciliation.
  const changePicture = useCallback(async (pictureId: string) => {
    try {
      await callApi(
        { method: "post", url: `content-factory/accounts/${accountId}/posts/${post.id}/visual`, data: { pictureId } },
        true,
        true,
      )
      onUpdated({ ...post, regeneratingVisual: true })
    } catch (err) {
      // 400/409 carry the server's reason (unusable picture, already published) — show it as-is.
      if (isValidationError(err)) {
        addToast(err.message, "error")
        return
      }
      console.error("Failed to change picture", { accountId, postId: post.id, pictureId, err })
      addToast("Failed to change the picture.", "error")
    }
  }, [accountId, post, addToast, onUpdated])

  // A series pick regenerates every episode, so it goes through the same confirmation as Recreate.
  const handlePickPicture = useCallback(async (pictureId: string) => {
    setShowPicturePicker(false)
    if (post.seriesId) {
      setPendingPictureId(pictureId)
      setRecreateMode("series")
      return
    }
    await changePicture(pictureId)
  }, [post.seriesId, changePicture])

  // Recreate visual - fire the regeneration, then optimistically mark the post regenerating so the
  // loading state shows at once and the batch poll picks it up; the poll reconciles with the server
  // and swaps in the new visual when the job finishes.
  const handleRecreateVisual = useCallback(async () => {
    setRecreateMode(null)
    if (pendingPictureId) {
      const pictureId = pendingPictureId
      setPendingPictureId(null)
      await changePicture(pictureId)
      return
    }
    try {
      await callApi(
        { method: "post", url: `content-factory/accounts/${accountId}/posts/${post.id}/visual` },
        true,
        true,
      )
      onUpdated({ ...post, regeneratingVisual: true })
    } catch (err) {
      // 409 = the server-side guard found the post (or an episode in its series) already published.
      if ((err as { status?: number })?.status === 409) {
        setRecreateMode(post.seriesId ? "blockedSeries" : "blockedPost")
        return
      }
      console.error("Failed to recreate visual", { accountId, postId: post.id, err })
      addToast("Failed to recreate the visual.", "error")
    }
  }, [accountId, post, addToast, onUpdated, pendingPictureId, changePicture])

  const toggleExpanded = useCallback(() => {
    const next = !isExpanded
    setIsExpanded(next)
    // Collapsing shortens the page and the browser yanks the scroll position to
    // fit; return to the top of the result page so the user lands somewhere sane.
    if (!next) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }, [isExpanded])

  return (
    <>
      <styles.PostRowItem data-testid={`post-row-${post.id}`}>
        <styles.PostRowLine
          onClick={toggleExpanded}
          data-expanded={isExpanded}
          data-testid={`post-row-header-${post.id}`}
        >
          <styles.PostStat>
            {reserveSeriesSlot && (
              <styles.SeriesSlot data-testid={`post-series-slot-${post.id}`}>
                {episodeDescription && (
                  <Tooltip content={episodeDescription} interactive={false}>
                    <styles.SeriesBadge role="img" tabIndex={0} aria-label={episodeDescription} data-testid={`post-series-${post.id}`}>
                      <Layers size={13} />
                    </styles.SeriesBadge>
                  </Tooltip>
                )}
              </styles.SeriesSlot>
            )}
            {status === "draft" ? (
              <styles.ApprovePill
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleSchedule()
                }}
                disabled={isScheduling}
                aria-label="Approve draft"
                data-testid={`post-approve-${post.id}`}
              >
                <styles.ApprovePillDefault>Draft</styles.ApprovePillDefault>
                <styles.ApprovePillHover>
                  <CheckCircle size={14} />
                  Approve
                </styles.ApprovePillHover>
              </styles.ApprovePill>
            ) : (
              <styles.Badge
                $background={statusConfig.background}
                $color={statusConfig.color}
                data-testid={`post-status-${post.id}`}
              >
                {statusConfig.label}
              </styles.Badge>
            )}
            {contentTypeId && (
              <ContentTypeBadge
                contentTypeId={contentTypeId}
                postIdea={null}
                maxWidth={undefined}
                variant="dark"
                size="sm"
                data-testid={`post-category-${post.id}`}
              />
            )}
          </styles.PostStat>

          <styles.PostWhen data-testid={`post-date-${post.id}`}>
            {formatScheduledDate(post.date, timezone)}
          </styles.PostWhen>

          <styles.PostPreviewText data-testid={`post-preview-${post.id}`}>
            {previewText || <span style={{ fontStyle: "italic", color: theme.colors.text02 }}>No content</span>}
          </styles.PostPreviewText>

          {showDuplicate && (
            <styles.DuplicateSlot>
              <Tooltip content={renderWithLinks(post.duplicateMessage!, history)}>
                <styles.DuplicateBadge
                  aria-label="Potential duplicate"
                  data-testid={`post-duplicate-${post.id}`}
                >
                  D
                </styles.DuplicateBadge>
              </Tooltip>
            </styles.DuplicateSlot>
          )}

          <styles.PostRowActions>
            {status === "published" ? (
              <styles.HeaderIconButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleVisit()
                }}
                aria-label="Visit post"
                data-testid={`post-visit-${post.id}`}
              >
                <span className="icon-external" style={{ fontSize: 17 }} />
              </styles.HeaderIconButton>
            ) : (
              <>
                <styles.HeaderIconButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleEdit()
                  }}
                  aria-label="Edit post"
                  data-testid={`post-edit-${post.id}`}
                >
                  <span className="icon-edit" style={{ fontSize: 17 }} />
                </styles.HeaderIconButton>
                <styles.HeaderIconButton
                  type="button"
                  $danger
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowDeleteModal(true)
                  }}
                  aria-label="Delete post"
                  data-testid={`post-delete-${post.id}`}
                >
                  <span className="icon-delete" style={{ fontSize: 17 }} />
                </styles.HeaderIconButton>
              </>
            )}

            <styles.HeaderIconButton type="button" aria-label={isExpanded ? "Collapse" : "Expand"}>
              <styles.ExpandIcon isExpanded={isExpanded}>
                <ChevronDown size={17} />
              </styles.ExpandIcon>
            </styles.HeaderIconButton>
          </styles.PostRowActions>
        </styles.PostRowLine>

        <styles.PostRowDetailWrapper isOpen={isExpanded} data-testid={`post-row-content-${post.id}`}>
          <styles.PostRowDetailClip>
            <styles.PostRowDetail>
              <styles.PostDetailLayout>
                <styles.PostDetailSpacer aria-hidden />

                <styles.PostPreviewSlot>
                  <LinkedInPostPreview
                    postText={post.text}
                    postImage={post.image}
                    userName={accountName}
                    userAvatar={accountAvatar}
                    active={isExpanded}
                    onRegenerate={status === "published" ? undefined : openRecreateVisualModal}
                    onChangePicture={status === "published" || !profileId || !post.pictureEligible ? undefined : openPicturePickerModal}
                    isRegenerating={!!post.regeneratingVisual}
                    headerAction={sourceInfo}
                  />
                </styles.PostPreviewSlot>

                <styles.PostDetailButtons>
                  {status !== "published" && (
                    <styles.EditComposerButton
                      type="button"
                      onClick={handleEdit}
                      data-testid={`post-open-composer-${post.id}`}
                    >
                      <span className="icon-edit" style={{ fontSize: 15 }} />
                      Edit in composer
                    </styles.EditComposerButton>
                  )}

                  {status === "draft" && post.date && (
                    <styles.ApproveButton
                      type="button"
                      onClick={handleSchedule}
                      disabled={isScheduling}
                      data-testid={`post-approve-action-${post.id}`}
                    >
                      <CheckCircle size={17} />
                      Approve
                    </styles.ApproveButton>
                  )}

                  {status !== "draft" && (
                    <styles.PostStatusTag
                      $background={statusConfig.background}
                      $color={statusConfig.color}
                      data-testid={`post-body-status-${post.id}`}
                    >
                      {status === "published" ? <CheckCircle size={16} /> : <Clock size={15} />}
                      {statusConfig.label}
                    </styles.PostStatusTag>
                  )}
                </styles.PostDetailButtons>
              </styles.PostDetailLayout>
            </styles.PostRowDetail>
          </styles.PostRowDetailClip>
        </styles.PostRowDetailWrapper>
      </styles.PostRowItem>

      <DeletePostModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
      />

      <ConfirmModal
        modalName="schedule-error-modal"
        title="Can't approve this post yet"
        description={scheduleError ?? ""}
        isOpen={scheduleError !== null}
        onClose={() => setScheduleError(null)}
      />

      {recreateMode !== null && (
        <RecreateVisualModal
          isOpen
          mode={recreateMode}
          onConfirm={handleRecreateVisual}
          onClose={() => {
            setRecreateMode(null)
            setPendingPictureId(null)
          }}
        />
      )}

      {profileId && (
        <PicturePickerModal
          isOpen={showPicturePicker}
          accountId={accountId}
          profileId={profileId}
          currentPictureId={post.currentPictureId}
          onSelect={handlePickPicture}
          onClose={() => setShowPicturePicker(false)}
        />
      )}

      <ChannelListModal
        modalName="edit-post-channel-modal"
        isOpen={showEditPicker}
        title="Which post?"
        entries={editPickerEntries}
        onSelect={(entry) => {
          setShowEditPicker(false)
          openEditTarget(entry.target)
        }}
        onClose={() => setShowEditPicker(false)}
      />

      <ChannelListModal
        modalName="visit-post-channel-modal"
        isOpen={showVisitPicker}
        title="Which post?"
        entries={visitPickerEntries}
        onSelect={(entry) => {
          setShowVisitPicker(false)
          openExternalPost(entry.scheduledPost.sourceId, entry.channel)
        }}
        onClose={() => setShowVisitPicker(false)}
      />
    </>
  )
}

export default PostRow
